import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Button } from '@/components/ui/button';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import type { EvaluationTemplate } from '@/features/webinar/types';
import { Check, Star } from 'lucide-react';
import { useState } from 'react';
import { useWebinarT } from '@/hooks/useWebinarT';
import { useAppDispatch } from '@/app/hooks';
import { submitPublicEvaluation } from '@/features/live/liveThunks';
import { toast } from 'react-toastify';
import { cn } from '@/lib/utils';

export default function EvaluationForm({
  visualIdentity,
  evaluation,
  webinarId,
  sessionId,
  authorization,
}: {
  visualIdentity: WorkspaceVisualIdentity;
  evaluation: EvaluationTemplate;
  webinarId: number;
  sessionId: number;
  authorization: string;
}) {
  const { t } = useWebinarT('webinars');
  const dispatch = useAppDispatch();
  // number[] = identifiants d'options pour les questions à choix (SINGLE_CHOICE / MULTIPLE_CHOICE).
  const [responses, setResponses] = useState<Record<number, string | number | number[] | null>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const sortedQuestions = [...evaluation.questions].sort((a, b) => a.position - b.position);

  const allItemValues = sortedQuestions.map((q) => `item-${q.id}`);

  const [openValue, setOpenValue] = useState<string | string[]>(
    sortedQuestions.length ? `item-${sortedQuestions[0].id}` : '',
  );

  const openNextQuestion = (currentIndex: number) => {
    if (currentIndex + 1 < sortedQuestions.length) {
      const isOpeningLast = currentIndex + 1 === sortedQuestions.length - 1;
      setOpenValue(isOpeningLast ? allItemValues : `item-${sortedQuestions[currentIndex + 1].id}`);
    } else {
      setOpenValue('');
    }
  };

  const handleAccordionValueChange = (value: string | string[]) => {
    setOpenValue(value);
  };

  // Une question est répondue si elle a une valeur non vide (tableau d'options non vide inclus).
  const hasAnswer = (questionId: number) => {
    const response = responses[questionId];
    if (response == null) return false;
    if (Array.isArray(response)) return response.length > 0;
    if (typeof response === 'string') return response.trim().length > 0;
    return true;
  };

  const hasResponses = sortedQuestions.some((q) => hasAnswer(q.id));

  const handleResponseChange = (questionId: number, value: string | number | number[] | null) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  // Bascule une option pour une question à réponses multiples.
  const toggleOption = (questionId: number, optionId: number) => {
    setResponses((prev) => {
      const current = Array.isArray(prev[questionId]) ? (prev[questionId] as number[]) : [];
      const next = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      return { ...prev, [questionId]: next };
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Construit le payload au contrat back : ratingValue / freeText / optionIds selon le type.
      const answers = sortedQuestions
        .filter((question) => hasAnswer(question.id))
        .map((question) => {
          const response = responses[question.id];
          if (question.type === 'SINGLE_CHOICE') {
            return { questionId: question.id, optionIds: [Number(response)] };
          }
          if (question.type === 'MULTIPLE_CHOICE') {
            return { questionId: question.id, optionIds: response as number[] };
          }
          if (question.type === 'TEXT_AREA') {
            return { questionId: question.id, freeText: String(response) };
          }
          return { questionId: question.id, ratingValue: Number(response) };
        });

      await dispatch(
        submitPublicEvaluation({
          webinarId,
          authorization,
          input: {
            sessionId,
            answers,
          },
        }),
      ).unwrap();

      setIsSubmitted(true);
      setOpenValue('');
      toast.success(t('thankyouPage.evaluation.successToast'));
      console.log('Responses submitted:', responses);
    } catch (error) {
      const appError = error as { code?: string; message?: string; details?: unknown };
      const errorMessage =
        appError.message &&
        (appError.message.startsWith('errors.') || appError.message.startsWith('HTTP.'))
          ? appError.message
          : appError.code
            ? t('thankyouPage.evaluation.errorWithCode', { code: appError.code })
            : t('thankyouPage.evaluation.errorToast');
      toast.error(errorMessage);
      console.error('Error submitting evaluation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestionContent = (
    question: (typeof evaluation.questions)[0],
    questionIndex: number,
  ) => {
    const response = responses[question.id];
    const isLastQuestion = questionIndex === sortedQuestions.length - 1;

    const goToNext = () => openNextQuestion(questionIndex);

    switch (question.type) {
      case 'SMILEYS':
        return (
          <div className="flex items-center gap-2">
            {['😫', '🙁', '🙂', '😁', '🥳'].map((emoji, index) => (
              <div
                key={index}
                className="text-2xl cursor-pointer"
                style={{ opacity: response === index ? 1 : 0.5 }}
                onClick={() => {
                  handleResponseChange(question.id, index);
                  goToNext();
                }}
              >
                {emoji}
              </div>
            ))}
          </div>
        );
      case 'THUMBS':
        return (
          <div className="flex items-center gap-2">
            <div
              className={`text-2xl cursor-pointer ${response === 0 ? '' : 'opacity-50'}`}
              onClick={() => {
                handleResponseChange(question.id, 0);
                goToNext();
              }}
            >
              👎
            </div>
            <div
              className={`text-2xl cursor-pointer ${response === 1 ? '' : 'opacity-50'}`}
              onClick={() => {
                handleResponseChange(question.id, 1);
                goToNext();
              }}
            >
              👍
            </div>
          </div>
        );
      case 'STARS':
        return (
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`cursor-pointer w-6 h-6 ${
                  response && Number(response) >= star
                    ? visualIdentity?.theme === 'DARK'
                      ? 'text-white fill-current'
                      : 'text-black fill-current'
                    : visualIdentity?.theme === 'DARK'
                      ? 'text-white'
                      : 'text-black'
                }`}
                onClick={() => {
                  handleResponseChange(question.id, star);
                  goToNext();
                }}
              />
            ))}
          </div>
        );
      case 'TEXT_AREA':
        return (
          <div className="space-y-3">
            <textarea
              placeholder={t('thankyouPage.evaluation.textAreaPlaceholder')}
              className="w-full h-20 px-4 py-2 border text-base border-input rounded-lg resize-none focus:outline-none"
              style={{
                backgroundColor:
                  visualIdentity?.theme === 'DARK'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(31, 31, 31, 0.03)',
                color: visualIdentity?.theme === 'DARK' ? 'white' : 'black',
              }}
              value={(response as string) || ''}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
            />
            {!isLastQuestion && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={goToNext}
                className={cn(
                  visualIdentity?.theme === 'DARK'
                    ? 'disabled:bg-button-primary-disable-inverse'
                    : '',
                )}
              >
                {t('thankyouPage.evaluation.continue')}
              </Button>
            )}
          </div>
        );
      case 'SINGLE_CHOICE': {
        const selectedOptionId = typeof response === 'number' ? response : null;
        return (
          <div className="space-y-2">
            {(question.options ?? []).map((option) => {
              const isSelected = selectedOptionId === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    if (option.id == null) return;
                    handleResponseChange(question.id, option.id);
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg border p-3 text-left',
                    visualIdentity?.theme === 'DARK'
                      ? 'border-white/20 text-white'
                      : 'border-input text-black',
                  )}
                  style={{
                    borderColor: isSelected ? visualIdentity?.accentColor || '#2c6cff' : undefined,
                  }}
                >
                  <span
                    className="flex size-5 shrink-0 items-center justify-center rounded-full border"
                    style={{
                      borderColor: visualIdentity?.accentColor || '#2c6cff',
                    }}
                  >
                    {isSelected && (
                      <span
                        className="size-2.5 rounded-full"
                        style={{ backgroundColor: visualIdentity?.accentColor || '#2c6cff' }}
                      />
                    )}
                  </span>
                  {option.textContent}
                </button>
              );
            })}
          </div>
        );
      }
      case 'MULTIPLE_CHOICE': {
        const selectedOptionIds = Array.isArray(response) ? response : [];
        return (
          <div className="space-y-2">
            {(question.options ?? []).map((option) => {
              const isSelected = option.id != null && selectedOptionIds.includes(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    if (option.id == null) return;
                    toggleOption(question.id, option.id);
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg border p-3 text-left',
                    visualIdentity?.theme === 'DARK'
                      ? 'border-white/20 text-white'
                      : 'border-input text-black',
                  )}
                  style={{
                    borderColor: isSelected ? visualIdentity?.accentColor || '#2c6cff' : undefined,
                  }}
                >
                  <span
                    className="flex size-5 shrink-0 items-center justify-center rounded-[4px] border"
                    style={{
                      borderColor: visualIdentity?.accentColor || '#2c6cff',
                      backgroundColor: isSelected
                        ? visualIdentity?.accentColor || '#2c6cff'
                        : 'transparent',
                    }}
                  >
                    {isSelected && <Check className="size-3.5 text-white" />}
                  </span>
                  {option.textContent}
                </button>
              );
            })}
            {!isLastQuestion && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={goToNext}
                className={cn(
                  visualIdentity?.theme === 'DARK'
                    ? 'disabled:bg-button-primary-disable-inverse'
                    : '',
                )}
              >
                {t('thankyouPage.evaluation.continue')}
              </Button>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4 w-full">
      {Array.isArray(openValue) ? (
        <Accordion
          type="multiple"
          className="space-y-4"
          value={openValue}
          onValueChange={(value) => !isSubmitted && handleAccordionValueChange(value)}
        >
          {sortedQuestions.map((question, index) => (
            <AccordionItem
              key={question.id}
              value={`item-${question.id}`}
              hideSeparator
              className="bg-medium rounded-[20px] px-4 text-base"
              style={{
                backgroundColor:
                  visualIdentity?.theme === 'LIGHT'
                    ? 'rgba(31, 31, 31, 0.03)'
                    : 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <AccordionTrigger
                disabled={isSubmitted}
                className={`${visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}`}
                chevronColor={visualIdentity?.contrastColor || '#2C6CFF'}
                isEvaluation
                hasResponse={hasAnswer(question.id)}
              >
                {question.text}
              </AccordionTrigger>
              <AccordionContent>{renderQuestionContent(question, index)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <Accordion
          type="single"
          collapsible
          className="space-y-4"
          value={openValue}
          onValueChange={(value) => !isSubmitted && handleAccordionValueChange(value)}
        >
          {sortedQuestions.map((question, index) => (
            <AccordionItem
              key={question.id}
              value={`item-${question.id}`}
              hideSeparator
              className="bg-medium rounded-[20px] px-4 text-base"
              style={{
                backgroundColor:
                  visualIdentity?.theme === 'LIGHT'
                    ? 'rgba(31, 31, 31, 0.03)'
                    : 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <AccordionTrigger
                disabled={isSubmitted}
                className={`${visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}`}
                chevronColor={visualIdentity?.contrastColor || '#2C6CFF'}
                isEvaluation
                hasResponse={hasAnswer(question.id)}
              >
                {question.text}
              </AccordionTrigger>
              <AccordionContent>{renderQuestionContent(question, index)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {isSubmitted ? (
        <p
          className={cn(
            'text-sm font-medium',
            visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black',
          )}
        >
          {t('thankyouPage.evaluation.submitted')}
        </p>
      ) : (
        <Button
          className={cn(
            'w-1/2 h-11',
            visualIdentity?.theme === 'DARK'
              ? 'disabled:bg-button-primary-disable-inverse '
              : 'disabled:bg-button-primary-disable!',
          )}
          style={{
            backgroundColor: visualIdentity?.accentColor || '#2c6cff',
            fontFamily: visualIdentity?.fontFamily || 'Poppins',
          }}
          onClick={handleSubmit}
          disabled={isSubmitting || !hasResponses}
        >
          {t(isSubmitting ? 'thankyouPage.evaluation.sending' : 'thankyouPage.evaluation.submit')}
        </Button>
      )}
    </div>
  );
}
